import type { IKeyAny } from 'helpers/url';
import type { RequestResult } from 'interfaces/api/client';

import { BaseApi } from '../base-api';

import type {
  IApiAccessTokenInfo,
  IApiCreateOauthClientPayload,
  IApiCreatePersonalAccessTokenPayload,
  IApiCreatePersonalAccessTokenResponse,
  IApiOauthClientConfiguration,
  IApiOauthClientScope,
  IApiPersonalAccessToken,
  IApiScopeCatalogEntry,
  IApiSetOauthClientScopesResponse,
  IApiUpdateOauthClientPayload,
} from './interfaces';

export class SsoApi extends BaseApi {
  protected readonly prefix = '';
  protected readonly version = '2';

  listScopes = (): Promise<RequestResult<IApiScopeCatalogEntry[]>> => this.get('scopes');

  listPersonalAccessTokens = (): Promise<RequestResult<IApiPersonalAccessToken[]>> => this.get('personal_token');

  createPersonalAccessToken = (
    payload: IApiCreatePersonalAccessTokenPayload,
  ): Promise<RequestResult<IApiCreatePersonalAccessTokenResponse>> => this.post('personal_token', { ...payload });

  deletePersonalAccessToken = (id: string): Promise<RequestResult<unknown>> =>
    this.delete(`personal_token/${encodeURIComponent(id)}`);

  /**
   * Resolves a personal access token's details by authenticating as its owner.
   * Uses a per-request Basic auth header (base64(email:token)) instead of the session token.
   * Hits the accounts client directly because this endpoint is served without the /v2 prefix.
   */
  getPersonalAccessTokenInfo = (email: string, token: string): Promise<RequestResult<IApiPersonalAccessToken>> =>
    this.api.get<IApiPersonalAccessToken>('personal_token/info', undefined, {
      Authorization: `Basic ${btoa(`${email}:${token}`)}`,
    });

  /**
   * Resolves an OAuth access token's details for the provided token (not the authorized user),
   * via a per-request Bearer header carrying the token being checked.
   */
  getAccessTokenInfo = (token: string): Promise<RequestResult<IApiAccessTokenInfo>> =>
    this.get('info', undefined, { Authorization: `Bearer ${token}` });

  listOauthClients = (): Promise<RequestResult<IApiOauthClientConfiguration[]>> => this.get('clients');

  getOauthClient = (id: string): Promise<RequestResult<IApiOauthClientConfiguration>> =>
    this.get(`clients/${encodeURIComponent(id)}`);

  createOauthClient = (payload: IApiCreateOauthClientPayload): Promise<RequestResult<IApiOauthClientConfiguration>> =>
    this.post('clients', { ...payload });

  updateOauthClient = (
    id: string,
    payload: IApiUpdateOauthClientPayload,
  ): Promise<RequestResult<IApiOauthClientConfiguration>> =>
    this.patch(`clients/${encodeURIComponent(id)}`, { ...payload });

  deleteOauthClient = (id: string): Promise<RequestResult<unknown>> => this.delete(`clients/${encodeURIComponent(id)}`);

  setOauthClientScopes = (
    id: string,
    scopes: IApiOauthClientScope[],
  ): Promise<RequestResult<IApiSetOauthClientScopesResponse>> =>
    this.put(`clients/${encodeURIComponent(id)}/scopes`, scopes as unknown as IKeyAny);
}
